import {graphql, useFragment} from 'react-relay'

import type {
  IssueEventConfidenceLevel,
  IssueTypeColor,
  PendingIssueSuggestionState,
  useRecentAgentActivity_issue$data,
  useRecentAgentActivity_issue$key,
} from './__generated__/useRecentAgentActivity_issue.graphql'

export type AgentActionType = 'label_added' | 'type_set' | 'field_set'

export type PendingSuggestionState = PendingIssueSuggestionState

export type SuggestionConfidence = 'HIGH' | 'MEDIUM' | 'LOW'

export type PendingSuggestion = {
  type: AgentActionType
  value: string | null
  color: string | null
  state: PendingSuggestionState
  confidence: SuggestionConfidence | null
  intentId: string | null
  actorId: string
  actorTypename: string
  actorLogin: string
  actorAvatarUrl: string
  actorDisplayName: string | null
  rationale: string | null
  // Used as the label shown on the row (e.g. "Type", "Label", or a field name)
  displayFieldName: string
  // Type suggestions only — used to wire up apply/reject mutations.
  issueTypeId: string | null
  issueTypeName: string | null
  issueTypeColor: IssueTypeColor | null
  // Label suggestions only.
  labelId: string | null
  // Field suggestions only.
  issueFieldId: string | null
}

export type AgentSuggestionsGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  suggestions: PendingSuggestion[]
}

export type AgentSuggestionsResult = {
  hasPendingSuggestions: boolean
  groups: AgentSuggestionsGroup[]
  totalSuggestionCount: number
}

export type RecentAgentActivityResult = {
  issueId: string
  viewerCanUpdateMetadata: boolean
  repoNameWithOwner: string
  pendingSuggestions: AgentSuggestionsResult
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const recentAgentActivityFragment = graphql`
  fragment useRecentAgentActivity_issue on Issue {
    id
    viewerCanUpdateMetadata
    repository {
      nameWithOwner
    }
    pendingSuggestions {
      __typename
      ... on PendingTypeSuggestion {
        typeState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        issueType {
          id
          name
          color
        }
      }
      ... on PendingLabelSuggestion {
        labelState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        label {
          id
          name
          color
        }
      }
      ... on PendingFieldSuggestion {
        fieldState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        suggestedValue
        confidence
        issueField {
          ... on Node {
            id
          }
          ... on IssueFieldCommon {
            name
          }
          ... on IssueFieldSingleSelect {
            options {
              name
              color
            }
          }
        }
      }
    }
  }
`

function normalizeConfidence(value: IssueEventConfidenceLevel | null | undefined): SuggestionConfidence | null {
  if (value === 'HIGH' || value === 'MEDIUM' || value === 'LOW') return value
  return null
}

export function processPendingSuggestions(
  suggestions: useRecentAgentActivity_issue$data['pendingSuggestions'],
): AgentSuggestionsResult {
  const groupsBySlug = new Map<string, AgentSuggestionsGroup>()
  let totalSuggestionCount = 0

  if (!suggestions) {
    return {
      hasPendingSuggestions: false,
      groups: [],
      totalSuggestionCount: 0,
    }
  }

  for (const suggestion of suggestions) {
    if (
      !suggestion ||
      (suggestion.__typename !== 'PendingTypeSuggestion' &&
        suggestion.__typename !== 'PendingLabelSuggestion' &&
        suggestion.__typename !== 'PendingFieldSuggestion')
    ) {
      continue
    }
    if (!suggestion.actor) continue

    // The server returns suggestions in all states (PENDING / APPLIED / REJECTED).
    // The panel only renders PENDING. Transient APPLIED chips shown immediately
    // after a viewer approves are tracked locally in the component instead.
    const suggestionState =
      suggestion.__typename === 'PendingTypeSuggestion'
        ? suggestion.typeState
        : suggestion.__typename === 'PendingLabelSuggestion'
          ? suggestion.labelState
          : suggestion.fieldState
    if (suggestionState && suggestionState !== 'PENDING') continue

    const actor = suggestion.actor
    const actorDisplayName = actor.__typename === 'Bot' ? (actor.displayName ?? null) : null

    let group = groupsBySlug.get(actor.login)
    if (!group) {
      group = {
        agentName: actor.login,
        agentSlug: actor.login,
        actorLogin: actor.login,
        actorAvatarUrl: actor.avatarUrl,
        suggestions: [],
      }
      groupsBySlug.set(actor.login, group)
    }

    const baseSuggestion = {
      actorId: actor.id,
      actorTypename: actor.__typename,
      actorLogin: actor.login,
      actorAvatarUrl: actor.avatarUrl,
      actorDisplayName,
      rationale: suggestion.rationale ?? null,
      confidence: normalizeConfidence(suggestion.confidence),
      intentId: suggestion.intent?.intentId ?? null,
    }

    if (suggestion.__typename === 'PendingTypeSuggestion' && suggestion.issueType) {
      const issueType = suggestion.issueType
      group.suggestions.push({
        ...baseSuggestion,
        type: 'type_set',
        state: suggestion.typeState ?? 'PENDING',
        displayFieldName: 'Type',
        value: issueType.name,
        color: issueType.color ?? null,
        issueTypeId: issueType.id,
        issueTypeName: issueType.name,
        issueTypeColor: issueType.color ?? null,
        labelId: null,
        issueFieldId: null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingLabelSuggestion' && suggestion.label) {
      const label = suggestion.label
      group.suggestions.push({
        ...baseSuggestion,
        type: 'label_added',
        state: suggestion.labelState ?? 'PENDING',
        displayFieldName: 'Label',
        value: label.name,
        color: label.color ?? null,
        issueTypeId: null,
        issueTypeName: null,
        issueTypeColor: null,
        labelId: label.id,
        issueFieldId: null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingFieldSuggestion' && suggestion.issueField) {
      const issueField = suggestion.issueField
      const matchingOption =
        suggestion.suggestedValue && issueField.options
          ? issueField.options.find(o => o.name === suggestion.suggestedValue)
          : undefined
      group.suggestions.push({
        ...baseSuggestion,
        type: 'field_set',
        state: suggestion.fieldState ?? 'PENDING',
        displayFieldName: issueField.name ?? 'Field',
        value: suggestion.suggestedValue ?? null,
        color: matchingOption?.color ?? null,
        issueTypeId: null,
        issueTypeName: null,
        issueTypeColor: null,
        labelId: null,
        issueFieldId: issueField.id ?? null,
      })
      totalSuggestionCount++
    }
  }

  const groups = Array.from(groupsBySlug.values())
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))

  return {
    hasPendingSuggestions: totalSuggestionCount > 0,
    groups,
    totalSuggestionCount,
  }
}

export function useRecentAgentActivity(issueRef: useRecentAgentActivity_issue$key): RecentAgentActivityResult {
  const data = useFragment(recentAgentActivityFragment, issueRef)
  const pendingSuggestions = processPendingSuggestions(data.pendingSuggestions ?? [])
  return {
    issueId: data.id,
    viewerCanUpdateMetadata: data.viewerCanUpdateMetadata ?? false,
    repoNameWithOwner: data.repository.nameWithOwner,
    pendingSuggestions,
  }
}
